import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Activity,
  ArrowRight,
  Calculator,
  Check,
  ChevronDown,
  Droplets,
  Dumbbell,
  Package,
  Plus,
  Quote,
  Ruler,
  ShoppingBag,
  Sparkles,
  TrendingUp,
} from "lucide-react";

import { listPublicPackagesOptions } from "@/actions/coaching-package/handler";
import { listPublishedEssaysOptions } from "@/actions/essay/handler";
import { listActiveSlidesOptions } from "@/actions/hero-slide/handler";
import { listPublicProductsOptions } from "@/actions/product/handler";
import { AnimatedSection } from "@/components/AnimatedSection";
import CardSwap, { Card as SwapCard } from "@/components/CardSwap";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

import { cn } from "@/lib/utils";

// Default hero slides fallback
const defaultSlides = [
  { url: "/bg.webp" },
  { url: "/DSC01273-2.webp" },
  { url: "/DSC01033.webp" },
  { url: "/DSC01363.webp" },
];

// Calculator data
const calculators = [
  {
    to: "/essay/1rm-calculator",
    icon: Dumbbell,
    title: "1RM Hesaplayıcı",
    description:
      "Antrenmanlarınızda kullanacağınız ağırlıkları belirlemek için tek tekrar maksimum gücünüzü bilimsel formüllerle hesaplayın.",
    tag: "Powerlifting",
  },
  {
    to: "/essay/mev-mrv-calculator",
    icon: Calculator,
    title: "MEV / MRV",
    description:
      "Gelişiminiz için gerekli olan minimum etkili hacim ve maksimum toparlanabilir hacim değerlerini keşfedin.",
    tag: "Hacim Planlama",
    passive: true,
  },
  {
    to: "/essay/calori-calculator",
    icon: Calculator,
    title: "Kalori Hesaplayıcı",
    description:
      "Hedefinize (kilo alma, verme veya koruma) uygun günlük kalori ve makro besin ihtiyaçlarınızı detaylıca planlayın.",
    tag: "Beslenme",
  },
  {
    to: "/essay/wilks-calculator",
    icon: TrendingUp,
    title: "Wilks Skoru",
    description:
      "Vücut ağırlığınızdan bağımsız olarak powerlifting performansınızı standartlaştırılmış puanlama sistemiyle karşılaştırın.",
    tag: "Powerlifting",
  },
  {
    to: "/essay/body-fat-calculator",
    icon: Ruler,
    title: "Body Fat",
    description:
      "Vücut ölçülerinizi kullanarak yağ oranınızı ve ideal kilonuzu güvenilir yöntemlerle analiz edin.",
    tag: "Vücut Analizi",
  },
  {
    to: "/essay/ffmi-calculator",
    icon: Activity,
    title: "FFMI Hesaplayıcı",
    description:
      "Yağsız kütle indeksinizi hesaplayarak genetik potansiyelinizi ve kas kütlesi gelişiminizi profesyonelce değerlendirin.",
    tag: "Vücut Analizi",
  },
  {
    to: "/essay/water-calculator",
    icon: Droplets,
    title: "Su İhtiyacı",
    description:
      "Antrenman yoğunluğu ve günlük aktivitenize göre ideal su tüketim miktarınızı belirleyin.",
    tag: "Hidrasyon",
  },
];

// Section header component with anime.js animation
function SectionHeader({
  title,
  caption,
  className,
}: {
  title: string;
  caption: string;
  className?: string;
}) {
  const ref = useScrollAnimation<HTMLDivElement>({ type: "fadeUp", delay: 0 });

  return (
    <div
      ref={ref}
      className={cn(
        "mb-8 px-2 text-center sm:mb-10 xl:mb-14 2xl:mb-16",
        className,
      )}
    >
      <h2 className="mb-2 font-bold text-2xl text-foreground tracking-tight sm:mb-3 sm:text-3xl md:text-4xl xl:text-5xl 2xl:text-6xl">
        {title}
      </h2>
      <p className="mx-auto max-w-2xl text-base text-muted-foreground sm:text-lg xl:max-w-3xl xl:text-xl 2xl:text-2xl">
        {caption}
      </p>
    </div>
  );
}

// CardSwap Hero Section with anime.js
function HeroSection() {
  const { data } = useSuspenseQuery(listActiveSlidesOptions());
  const titleRef = useScrollAnimation<HTMLHeadingElement>({
    type: "fadeUp",
    delay: 200,
  });
  const subtitleRef = useScrollAnimation<HTMLParagraphElement>({
    type: "fadeUp",
    delay: 400,
  });
  const buttonsRef = useScrollAnimation<HTMLDivElement>({
    type: "fadeUp",
    delay: 600,
  });

  const slides =
    data.body?.slides.length > 0
      ? data.body.slides.map((slide) => ({ url: slide.url }))
      : defaultSlides;

  return (
    <AnimatedSection
      className="relative flex min-h-[600px] items-center overflow-hidden bg-background py-20 lg:py-32"
      animationType="fadeIn"
      delay={200}
    >
      <div className="container relative z-10 mx-auto flex items-center justify-between gap-4 px-4 lg:justify-center lg:gap-20 lg:px-6 xl:gap-32 2xl:gap-40">
        {/* Left: Content */}
        <div className="max-w-[55%] lg:max-w-xl xl:max-w-2xl 2xl:max-w-3xl">
          <h1
            ref={titleRef}
            className="mb-3 font-black text-foreground text-xl uppercase leading-[1] tracking-tight sm:text-2xl md:text-3xl lg:mb-6 lg:text-6xl xl:text-7xl 2xl:text-8xl"
          >
            MEVZU
            <br />
            <span className="text-primary">BAHİS</span> OLAN
            <br />
            GELİŞİM
          </h1>
          <p
            ref={subtitleRef}
            className="mb-4 text-muted-foreground text-xs tracking-wide sm:text-sm lg:mb-8 lg:text-xl xl:text-2xl 2xl:text-3xl"
          >
            Kibir yok, her daim sonsuz huzursuzluk var.
          </p>

          <div
            ref={buttonsRef}
            className="flex flex-col gap-2 lg:flex-row lg:gap-4"
          >
            <Button
              asChild
              size="sm"
              className="uppercase tracking-wide lg:size-lg xl:h-14 xl:px-8 xl:text-lg"
            >
              <Link to="/coachs">Koçluk Paketleri</Link>
            </Button>
            <Button
              asChild
              size="sm"
              variant="outline"
              className="uppercase tracking-wide lg:size-lg xl:h-14 xl:px-8 xl:text-lg"
            >
              <Link to="/essay" className="text-foreground">
                Hesaplayıcılar
              </Link>
            </Button>
          </div>
        </div>

        {/* Mobile: Vertical Marquee */}
        <div className="relative h-[70vh] w-[40%] overflow-hidden lg:hidden">
          <div className="flex animate-marquee-vertical flex-col gap-3">
            {[...slides, ...slides].map((slide, index) => (
              <div
                key={index}
                className="h-[180px] w-full shrink-0 overflow-hidden rounded-lg"
              >
                <img
                  src={slide.url}
                  alt={`Slide ${index + 1}`}
                  className="h-full w-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Desktop: CardSwap */}
        <div className="-translate-y-36 2xl:-translate-y-60 relative hidden h-[550px] w-[500px] lg:block xl:h-[700px] xl:w-[600px] 2xl:h-[850px] 2xl:w-[750px]">
          <CardSwap
            width={550}
            height={520}
            cardDistance={10}
            verticalDistance={10}
            delay={5000}
            pauseOnHover={true}
            skewAmount={6}
            easing="elastic"
          >
            {slides.slice(0, 4).map((slide, index) => (
              <SwapCard key={index} className="overflow-hidden">
                <img
                  src={slide.url}
                  alt={`Slide ${index + 1}`}
                  className="h-full w-full object-cover"
                />
              </SwapCard>
            ))}
          </CardSwap>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="-translate-x-1/2 absolute bottom-8 left-1/2 hidden animate-bounce lg:block">
        <ChevronDown className="h-8 w-8 text-muted-foreground/50 xl:h-10 xl:w-10" />
      </div>
    </AnimatedSection>
  );
}

// Content/Essays Section
function ContentSection() {
  const { data } = useSuspenseQuery(listPublishedEssaysOptions());
  const essays = data.body?.essays.slice(0, 3) || [];
  const gridRef = useScrollAnimation<HTMLDivElement>({
    type: "fadeUp",
    delay: 200,
  });

  const formatDate = (date: Date | null) => {
    if (!date) return "";
    return new Intl.DateTimeFormat("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(date));
  };

  return (
    <AnimatedSection
      className="flex flex-col justify-center bg-muted/30 px-4 py-20 sm:px-8 lg:py-32 xl:px-12 2xl:px-16"
      animationType="fadeUp"
      delay={100}
    >
      <div className="mx-auto w-full max-w-6xl xl:max-w-7xl 2xl:max-w-[1600px]">
        <SectionHeader
          title="İçerikler"
          caption="Spor, beslenme ve performans üzerine yazılar ve rehberler"
        />

        {essays.length > 0 ? (
          <div
            ref={gridRef}
            className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4 xl:gap-6 2xl:gap-8"
          >
            {essays.slice(0, 5).map((essay) => (
              <Link
                key={essay.essayId}
                to="/essay/$essayId"
                params={{ essayId: essay.essayId }}
                className="group h-full"
              >
                <Card className="hover:-translate-y-1 relative flex h-full flex-col overflow-hidden border-border/50 bg-background/50 backdrop-blur-sm transition-all duration-500 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/5">
                  <div className="absolute inset-0 bg-linear-to-br from-primary/5 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                  {essay.coverImageUrl && (
                    <div className="relative h-24 overflow-hidden sm:h-32">
                      <img
                        src={essay.coverImageUrl}
                        alt={essay.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                  )}

                  <CardHeader className="relative z-10 p-3 pb-0 sm:p-5 sm:pb-1">
                    <CardTitle className="line-clamp-2 font-bold text-foreground text-sm tracking-tight transition-colors group-hover:text-primary sm:text-base">
                      {essay.title}
                    </CardTitle>
                    <CardDescription className="mt-1 text-[10px] text-muted-foreground/70 sm:text-xs">
                      {formatDate(essay.publishedAt)} · {essay.readTime}
                    </CardDescription>
                  </CardHeader>

                  {essay.excerpt && (
                    <CardContent className="relative z-10 grow px-3 pb-3 sm:px-5 sm:pb-5">
                      <p className="line-clamp-2 text-[10px] text-muted-foreground/80 leading-relaxed sm:text-xs">
                        {essay.excerpt}
                      </p>
                    </CardContent>
                  )}
                </Card>
              </Link>
            ))}

            {/* View More Card */}
            <Link to="/essay" className="group h-full">
              <Card className="hover:-translate-y-1 relative flex h-full flex-col items-center justify-center border-border/50 border-dashed bg-muted/20 backdrop-blur-sm transition-all duration-500 hover:border-primary/50 hover:bg-primary/5 hover:shadow-xl">
                <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border transition-all duration-300 group-hover:scale-110 group-hover:border-primary group-hover:ring-primary/50 sm:mb-3 sm:h-16 sm:w-16">
                  <Plus className="h-5 w-5 text-primary/70 transition-colors group-hover:text-primary sm:h-8 sm:w-8" />
                </div>
                <h3 className="mb-1 font-bold text-foreground text-xs group-hover:text-primary sm:text-base">
                  Daha Fazla
                </h3>
                <p className="max-w-[120px] text-center text-[10px] text-muted-foreground leading-relaxed sm:text-xs">
                  Tüm içerikleri görüntüle
                </p>
              </Card>
            </Link>
          </div>
        ) : (
          <p className="text-center text-muted-foreground">
            Henüz yayınlanmış içerik yok.
          </p>
        )}
      </div>
    </AnimatedSection>
  );
}

// Calculators Section
function CalculatorSection() {
  const gridRef = useScrollAnimation<HTMLDivElement>({
    type: "fadeUp",
    delay: 200,
  });

  return (
    <AnimatedSection
      className="flex flex-col justify-center bg-background px-4 py-20 sm:px-8 lg:py-32 xl:px-12 2xl:px-16"
      animationType="fadeUp"
      delay={100}
    >
      <div className="mx-auto w-full max-w-6xl xl:max-w-7xl 2xl:max-w-[1600px]">
        <SectionHeader
          title="Hesaplayıcılar"
          caption="Antrenman ve beslenme planlaması için ücretsiz araçlar"
        />

        <div
          ref={gridRef}
          className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4 xl:gap-6 2xl:gap-8"
        >
          {calculators
            .filter((c) => !c.passive)
            .slice(0, 5)
            .map((calc) => (
              <Link key={calc.to} to={calc.to} className="group h-full">
                <Card className="hover:-translate-y-1 relative flex h-full flex-col overflow-hidden border-border/50 bg-card backdrop-blur-sm transition-all duration-500 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/5">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                  <CardHeader className="relative z-10 p-3 pb-0 sm:p-5 sm:pb-1">
                    <div className="mb-2 flex items-start justify-between sm:mb-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/20 transition-all duration-300 group-hover:bg-primary group-hover:text-primary-foreground sm:h-10 sm:w-10">
                        <calc.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </div>
                    </div>

                    <CardTitle className="line-clamp-1 font-bold text-foreground text-sm tracking-tight transition-colors group-hover:text-primary sm:text-base">
                      {calc.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="relative z-10 grow px-3 pb-3 sm:px-5 sm:pb-5">
                    <p className="line-clamp-2 text-[10px] text-muted-foreground/80 leading-relaxed sm:text-xs">
                      {calc.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}

          {/* View More Card */}
          <Link to="/essay" className="group h-full">
            <Card className="hover:-translate-y-1 relative flex h-full flex-col items-center justify-center border-border/50 border-dashed bg-muted/20 backdrop-blur-sm transition-all duration-500 hover:border-primary/50 hover:bg-primary/5 hover:shadow-xl">
              <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border transition-all duration-300 group-hover:scale-110 group-hover:border-primary group-hover:ring-primary/50 sm:mb-3 sm:h-16 sm:w-16">
                <Plus className="h-5 w-5 text-primary/70 transition-colors group-hover:text-primary sm:h-8 sm:w-8" />
              </div>
              <h3 className="mb-1 font-bold text-foreground text-xs group-hover:text-primary sm:text-base">
                Daha Fazla
              </h3>
              <p className="max-w-[120px] text-center text-[10px] text-muted-foreground leading-relaxed sm:text-xs">
                Tüm araçları görüntüle
              </p>
            </Card>
          </Link>
        </div>
      </div>
    </AnimatedSection>
  );
}

// Pricing Section
function PricingSection() {
  const { data } = useSuspenseQuery(listPublicPackagesOptions());
  const packages = data.body?.coaching.slice(0, 2) || [];
  const gridRef = useScrollAnimation<HTMLDivElement>({
    type: "fadeUp",
    delay: 200,
  });

  return (
    <AnimatedSection
      className="flex flex-col justify-center bg-muted/30 px-4 py-20 sm:px-8 lg:py-32 xl:px-12 2xl:px-16"
      animationType="fadeUp"
      delay={100}
    >
      <div className="mx-auto w-full max-w-5xl xl:max-w-6xl 2xl:max-w-7xl">
        <SectionHeader
          title="Koçluk Paketleri"
          caption="Hedeflerinize ulaşmak için profesyonel destek"
        />

        {packages.length > 0 ? (
          <div
            ref={gridRef}
            className="grid gap-4 sm:gap-6 md:grid-cols-2 xl:gap-8 2xl:gap-10"
          >
            {packages.map((plan) => (
              <Card
                key={plan.title}
                className={cn(
                  "relative flex flex-col overflow-hidden transition-all duration-300 hover:shadow-xl",
                  plan.isRecommended && "border-primary",
                )}
              >
                {plan.isRecommended && (
                  <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
                    <Badge className="bg-primary px-2 py-0.5 text-[10px] text-primary-foreground sm:px-2.5 sm:py-0.5 sm:text-xs">
                      ÖNERİLEN
                    </Badge>
                  </div>
                )}

                {plan.imageUrl && (
                  <div className="h-32 overflow-hidden bg-muted/30 sm:h-40">
                    <img
                      src={plan.imageUrl}
                      alt={plan.title}
                      className="h-full w-full object-contain"
                    />
                  </div>
                )}

                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="text-lg sm:text-xl">
                    {plan.title}
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    {plan.description}
                  </CardDescription>

                  <div className="pt-3 sm:pt-4">
                    <div className="flex items-baseline gap-2">
                      {plan.oldPrice && plan.oldPrice !== plan.price && (
                        <span className="text-base text-muted-foreground line-through sm:text-lg">
                          ₺{plan.oldPrice.toLocaleString()}
                        </span>
                      )}
                      <span className="font-extrabold text-2xl text-primary sm:text-3xl">
                        ₺{plan.price.toLocaleString()}
                      </span>
                    </div>
                    <p className="mt-1 text-muted-foreground text-xs sm:text-sm">
                      {plan.priceDetails}
                    </p>
                  </div>
                </CardHeader>

                <CardContent className="grow px-4 pb-4 sm:px-6 sm:pb-6">
                  <ul className="space-y-2">
                    {plan.features.slice(0, 4).map((feature, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-xs sm:text-sm"
                      >
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary sm:h-4 sm:w-4" />
                        <span>{feature}</span>
                      </li>
                    ))}
                    {plan.features.length > 4 && (
                      <li className="text-muted-foreground text-xs sm:text-sm">
                        +{plan.features.length - 4} özellik daha
                      </li>
                    )}
                  </ul>
                </CardContent>

                <CardFooter className="border-t bg-muted/20 p-4 sm:p-6">
                  <Button asChild className="w-full" size="lg">
                    <a
                      href={plan.ctaLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {plan.ctaText}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        ) : (
          <p className="text-center text-muted-foreground">
            Henüz koçluk paketi eklenmemiş.
          </p>
        )}

        <div className="mt-8 text-center sm:mt-10 xl:mt-14">
          <Button
            asChild
            variant="outline"
            size="lg"
            className="w-full sm:w-auto xl:h-14 xl:px-10 xl:text-lg"
          >
            <Link to="/coachs" className="group text-foreground">
              Tüm Paketleri Gör
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1 xl:h-5 xl:w-5" />
            </Link>
          </Button>
        </div>
      </div>
    </AnimatedSection>
  );
}

// Store Section
function StoreSection() {
  const { data } = useSuspenseQuery(listPublicProductsOptions());
  const products = data.body?.all?.slice(0, 4) || [];
  const gridRef = useScrollAnimation<HTMLDivElement>({
    type: "fadeUp",
    delay: 200,
  });

  return (
    <AnimatedSection
      className="flex flex-col justify-center bg-background px-4 py-20 sm:px-8 lg:py-32 xl:px-12 2xl:px-16"
      animationType="fadeUp"
      delay={100}
    >
      <div className="mx-auto w-full max-w-6xl xl:max-w-7xl 2xl:max-w-[1600px]">
        <SectionHeader
          title="Mağaza"
          caption="Spor yaşamınız için kaliteli ürünler"
        />

        {products.length > 0 ? (
          <div
            ref={gridRef}
            className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4 xl:gap-6 2xl:gap-8"
          >
            {products.map((product: any) => (
              <a
                key={product.productId}
                href={product.shopierLink}
                target="_blank"
                rel="noopener noreferrer"
                className="group h-full"
              >
                <Card className="hover:-translate-y-1 relative flex h-full flex-col overflow-hidden border-border/50 bg-card backdrop-blur-sm transition-all duration-500 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/5">
                  {product.isFeatured && (
                    <div className="absolute top-2 right-2 z-10">
                      <Badge className="gap-1 bg-primary px-2 py-0.5 text-[10px] text-primary-foreground">
                        <Sparkles className="h-2.5 w-2.5" />
                        ÖNE ÇIKAN
                      </Badge>
                    </div>
                  )}

                  <div className="relative h-24 overflow-hidden bg-muted/30 sm:h-32">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Package className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  <CardHeader className="relative z-10 p-3 pb-0 sm:p-5 sm:pb-1">
                    <CardTitle className="line-clamp-2 font-bold text-foreground text-sm tracking-tight transition-colors group-hover:text-primary sm:text-base">
                      {product.name}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="relative z-10 grow px-3 pb-3 sm:px-5 sm:pb-5">
                    <div className="flex items-baseline gap-2">
                      {product.oldPrice &&
                        product.oldPrice !== product.price && (
                          <span className="text-[10px] text-muted-foreground line-through sm:text-xs">
                            ₺{product.oldPrice.toLocaleString()}
                          </span>
                        )}
                      <span className="font-extrabold text-lg text-primary sm:text-xl">
                        ₺{product.price.toLocaleString()}
                      </span>
                    </div>
                  </CardContent>

                  <CardFooter className="border-t bg-muted/20 p-3 sm:p-4">
                    <Button size="sm" className="w-full gap-2">
                      <ShoppingBag className="h-3.5 w-3.5" />
                      Satın Al
                    </Button>
                  </CardFooter>
                </Card>
              </a>
            ))}

            {/* View More Card */}
            <Link to="/store" className="group h-full">
              <Card className="hover:-translate-y-1 relative flex h-full flex-col items-center justify-center border-border/50 border-dashed bg-muted/20 backdrop-blur-sm transition-all duration-500 hover:border-primary/50 hover:bg-primary/5 hover:shadow-xl">
                <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border transition-all duration-300 group-hover:scale-110 group-hover:border-primary group-hover:ring-primary/50 sm:mb-3 sm:h-16 sm:w-16">
                  <Plus className="h-5 w-5 text-primary/70 transition-colors group-hover:text-primary sm:h-8 sm:w-8" />
                </div>
                <h3 className="mb-1 font-bold text-foreground text-xs group-hover:text-primary sm:text-base">
                  Daha Fazla
                </h3>
                <p className="max-w-[120px] text-center text-[10px] text-muted-foreground leading-relaxed sm:text-xs">
                  Tüm ürünleri görüntüle
                </p>
              </Card>
            </Link>
          </div>
        ) : (
          <p className="text-center text-muted-foreground">
            Henüz ürün eklenmemiş.
          </p>
        )}

        <div className="mt-8 text-center sm:mt-10 xl:mt-14">
          <Button
            asChild
            variant="outline"
            size="lg"
            className="w-full sm:w-auto xl:h-14 xl:px-10 xl:text-lg"
          >
            <Link to="/store" className="group text-foreground">
              Mağazaya Git
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1 xl:h-5 xl:w-5" />
            </Link>
          </Button>
        </div>
      </div>
    </AnimatedSection>
  );
}

// About, Mission & Vision Section
function AboutMissionSection() {
  const imageRef = useScrollAnimation<HTMLDivElement>({
    type: "fadeUp",
    delay: 100,
  });
  const contentRef = useScrollAnimation<HTMLDivElement>({
    type: "fadeUp",
    delay: 200,
  });

  const certifications = [
    {
      title: "IPF Level II",
      desc: "Uluslararası Powerlifting Federasyonu 2. Kademe",
    },
    {
      title: "EREPS Level 3",
      desc: "Avrupa Sağlık ve Fitness Derneği Onaylı Antrenörlük",
    },
    {
      title: "RTS Programming",
      desc: "Reactive Training Systems Bilimsel Programlama",
    },
    {
      title: "M&M Anterior Knee Pain",
      desc: "Diz Biyomekaniği ve Sakatlık Önleme",
    },
    {
      title: "İstanbul Üniversitesi",
      desc: "Antrenörlük Sertifikası",
    },
    {
      title: "İstanbul Üniversitesi",
      desc: "Kuvvet ve Kondisyon Koçluğu",
    },
  ];

  return (
    <AnimatedSection
      id="misyon-vizyon"
      className="flex flex-col justify-center bg-muted/30 px-4 py-12 sm:px-8 lg:py-16 xl:px-12 2xl:px-16"
      animationType="fadeUp"
      delay={100}
    >
      <div className="mx-auto w-full max-w-6xl xl:max-w-7xl 2xl:max-w-[1600px]">
        <SectionHeader
          title="Misyonum, Vizyonum & Öz Geçmişim"
          caption="En iyi şekilde uygulama, uygulatma ve teşvik etme uğraşında kişi"
          className="mb-8 sm:mb-10"
        />

        <div className="grid items-start gap-8 lg:grid-cols-12 lg:gap-12">
          {/* Sol: Fotoğraf & Felsefe */}
          <div ref={imageRef} className="space-y-4 lg:col-span-5">
            <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted sm:aspect-[16/11] lg:aspect-[4/4.5]">
                <img
                  src="/DSC01363.webp"
                  alt="Alican Atalay"
                  className="h-full w-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute right-4 bottom-4 left-4 space-y-1 text-white">
                  <div className="font-extrabold text-xl tracking-tight sm:text-2xl">
                    Alican Atalay
                  </div>
                  <div className="font-medium text-xs text-zinc-300">
                    IPF Koçu ve Atleti
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-3">
                <Quote className="h-4 w-4 shrink-0 rotate-180 text-muted-foreground" />
                <p className="font-semibold text-foreground text-xs italic leading-relaxed sm:text-sm">
                  "Sağlam vücutta, sağlam kafa bulunur."
                </p>
              </div>
            </div>
          </div>

          {/* Sağ: Hikaye, Eğitimler, Misyon & Vizyon */}
          <div ref={contentRef} className="space-y-6 lg:col-span-7">
            {/* Kendi Yolumu Nasıl Buldum? */}
            <div className="space-y-2.5">
              <h3 className="font-bold text-foreground text-lg tracking-tight sm:text-xl">
                Kendi Yolumu Nasıl Buldum?
              </h3>
              <div className="space-y-2 text-muted-foreground text-xs leading-relaxed sm:text-sm">
                <p>
                  2020-21 yılları, geleceğe dair ne yapacağımı tam olarak
                  bilemediğim, yönümü bulmaya çalıştığım bir dönemdi. Bir
                  hedefim olmasa da çalışmak zorundaydım; bu yüzden bir
                  restoranda çalışmaya başladım ve emek vererek kendi birikimimi
                  oluşturdum. Aylar sonra dışarıdan bakma fırsatı bulduğumda,
                  şehir değiştirecek ve kendi düzenimi kuracak imkana sahiptim.
                  Daha da önemlisi, uğruna bir ömrü adayabileceğim bir amacım
                  vardı.
                </p>
                <p>
                  <strong className="text-foreground">
                    "Sağlam vücutta, sağlam kafa bulunur"
                  </strong>{" "}
                  felsefesini o dönemde tam anlamıyla benimsedim. Bir yandan
                  usta yazarları okuyup yazarak zihinsel disiplinimi
                  geliştirirken, diğer yandan ağırlık antrenmanlarıyla fiziksel
                  potansiyelimi keşfettim. Bu disiplin beni İstanbul
                  Üniversitesi'nde İngilizce Lojistik Yönetimi lisans eğitimine
                  ve{" "}
                  <span className="font-semibold text-foreground">
                    IPF -93 kg kategorisinde podyuma çıkan bir powerlifter
                  </span>{" "}
                  olmaya taşıdı. Ancak en büyük tutkum, bu yolculuğu ve
                  edindiğim disiplini başkalarına aktarabilmek, yani
                  antrenörlüktü.
                </p>
              </div>
            </div>

            {/* Eğitim ve Uzmanlığım */}
            <div className="space-y-2.5">
              <h3 className="font-bold text-base text-foreground tracking-tight sm:text-lg">
                Eğitim ve Uzmanlığım
              </h3>
              <p className="text-muted-foreground text-xs leading-relaxed">
                Saha tecrübemi akademik ve uluslararası düzeyde kanıtlanmış
                eğitimlerle destekledim. İngilizcem sayesinde küresel antrenman
                bilimini doğrudan programlarıma entegre ediyorum:
              </p>

              <div className="grid gap-2 sm:grid-cols-2">
                {certifications.map((cert, index) => (
                  <div
                    key={index}
                    className="flex flex-col justify-center rounded-lg border border-border bg-card p-3"
                  >
                    <div className="font-semibold text-foreground text-xs">
                      {cert.title}
                    </div>
                    <div className="text-[11px] text-muted-foreground leading-tight">
                      {cert.desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Misyon & Vizyon */}
            <div className="grid gap-3 pt-1 sm:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="mb-1 font-bold text-foreground text-sm">
                  Misyonum
                </div>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  Sadece profesyonel sporcuları değil, hayat kalitesini
                  artırmak, daha güçlü ve sağlıklı olmak isteyen herkesi
                  bilinçli egzersiz yapmaya davet etmek ve en doğru yöntemlerle
                  teşvik etmektir.
                </p>
              </div>

              <div className="rounded-xl border border-border bg-card p-4">
                <div className="mb-1 font-bold text-foreground text-sm">
                  Vizyonum
                </div>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  Antrenman bilimini herkes için erişilebilir kılan, sağlığı ve
                  gücü merkeze alan global bir topluluk inşa etmek ve
                  sürdürülebilir egzersiz alışkanlığını kazandıran öncü bir
                  marka olmaktır.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
}

// Main Landing Page
export function LandingPage() {
  return (
    <main className="w-full scroll-smooth">
      <HeroSection />
      <AboutMissionSection />
      <ContentSection />
      <CalculatorSection />
      <StoreSection />
      <PricingSection />
    </main>
  );
}
